import React, { useEffect, useState } from 'react';
import { Link, useLocation } from "react-router-dom";
import { LogOut, User, X, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "./ui/button";
import NavigationMenu from "./header/NavigationMenu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "./ui/use-toast";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";

const LegalHeader = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();

  const legalPages = ['/terms', '/disclaimer', '/privacy'];
  const isLegalPage = legalPages.includes(location.pathname);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsLoggedIn(!!session);
    };
    
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleLogout = async () => {
    if (isLoggingOut) return;

    try {
      setIsLoggingOut(true);
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your account.",
      });

      navigate('/');
      
    } catch (error: any) {
      console.error("Logout error:", error);
      toast({
        title: "Error logging out",
        description: error.message || "An error occurred while logging out",
        variant: "destructive",
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-gradient-to-r from-hero-dark to-hero-mid px-4 py-1 md:py-2 flex justify-between items-center h-12 md:h-14">
      <Link to="/" className="flex items-center">
        <img 
          src="/lovable-uploads/598844d9-6dcc-4334-ba58-d470d9deaa33.png" 
          alt="METRADE Logo" 
          className="h-6 md:h-7 w-auto brightness-0 invert"
        />
      </Link>
      <div className="flex items-center gap-2">
        {(isLoggedIn && !isMobile) ? (
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link to="/account" className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-200">
                    <User className="h-4 w-4 text-white" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-white/10 backdrop-blur-sm text-white border-white/20">
                  <p>My Account</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button 
                    onClick={handleLogout}
                    className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-200"
                    disabled={isLoggingOut}
                  >
                    <LogOut className="h-4 w-4 text-white" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-white/10 backdrop-blur-sm text-white border-white/20">
                  <p>Logout</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ) : (
          <div className="hidden md:flex items-center gap-2">
            <Link to="/auth">
              <Button variant="outline" className="text-white bg-transparent border-white hover:bg-white/10">
                Login
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="bg-white text-hero-dark hover:bg-white/90">
                Sign Up
              </Button>
            </Link>
          </div>
        )}
        {(!isMobile) && (
          <Sheet>
            <SheetTrigger asChild>
              <button className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-200 ml-1">
                <Menu className="h-4 w-4 text-white" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[350px] p-0 bg-gradient-to-b from-corporate-primary-100 to-corporate-primary-75 border-l border-white/10 shadow-xl">
              <NavigationMenu />
              <SheetClose className="absolute right-4 top-4 flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-200">
                <X className="h-4 w-4 text-white" />
              </SheetClose>
            </SheetContent>
          </Sheet>
        )}
      </div>
    </header>
  );
};

export default LegalHeader;
